import { linkChargingStation } from '@infuse/api/e-connector/charging-station';
import { useNotify } from '@infuse/cpms/ui';
import { LinkedCentralSystem } from '@infuse/e-connector/central-system/domain';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { createLinkRequestFor } from './useUpsertCentralSystemLink';

export interface CentralSystemLinkUpsert {
  linkedCentralSystem: LinkedCentralSystem;
  // passed in by the caller (hydrated rows already carry the name) to avoid a GET per link
  centralSystemName: string;
}

export const useBulkUpsertCentralSystemLinks = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyFailure } = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  return useMutation({
    mutationFn: async ({
      links,
      shouldRemoveLink = false
    }: {
      links: CentralSystemLinkUpsert[];
      shouldRemoveLink?: boolean;
    }) => {
      const results = await Promise.allSettled(
        links.map(({ linkedCentralSystem, centralSystemName }) =>
          linkChargingStation(
            { id: linkedCentralSystem.chargingStationId },
            createLinkRequestFor(linkedCentralSystem, centralSystemName, shouldRemoveLink)
          )
        )
      );

      const failed = links
        .filter((_, index) => results[index].status === 'rejected')
        .map((link) => link.centralSystemName);

      return { failed, shouldRemoveLink };
    },
    onSuccess: async ({ failed, shouldRemoveLink }) => {
      // some links may have succeeded even when others failed, so always refetch
      await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });

      const action = shouldRemoveLink ? 'bulkRemove' : 'bulkUpdate';

      if (failed.length === 0) {
        notifySuccess({
          title: t('e_connector.common.notify.success'),
          message: t(`e_connector.chargingStationLinks.notify.${action}.success`)
        });
      } else {
        notifyFailure({
          title: t('e_connector.common.notify.error'),
          message: t(`e_connector.chargingStationLinks.notify.${action}.error`, { names: failed.join(', ') })
        });
      }
    }
  });
};
